import { unwrap } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, EmptyState, ErrorState, Icon, Skeleton, buttonClasses } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceId } from "../../../../session/workspace-context";
import { CodeSnippet } from "./panel-kit";

const K = "privacyM29.webhooks";

/** Events whose payload carries `customer_ref` (backend `customer_context/webhook_ref.py`): conversation and message events and every `ticket.*`. */
const CONVERSATION_EVENTS: ReadonlySet<string> = new Set([
  "conversation.created",
  "conversation.handoff",
  "conversation.assigned",
  "conversation.status_changed",
  "conversation.rated",
  "message.created",
]);
const TICKET_PREFIX = "ticket.";

export const carriesCustomerRef = (event: string): boolean => CONVERSATION_EVENTS.has(event) || event.startsWith(TICKET_PREFIX);

/** The sample of the mockup (pin 4): IDs only, the reference your systems already use. */
export const SAMPLE_EVENT = JSON.stringify(
  { type: "conversation.handoff", conversation_id: "cnv_8Q2…", customer_ref: "c_7741…", reason: "suspected_fraud", created_at: "2026-10-29T09:14:02+08:00" },
  null,
  2,
);

function useWebhookEndpoints(enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: ["ws", wsId, "settings", "webhook-reference"] as const, queryFn: () => unwrap(api.GET("/v1/webhooks")), enabled });
}

function Header() {
  const { t } = useTranslation();
  return (
    <div className="card-header">
      <div className="min-w-0">
        <h2 id="webhook-ref-title" className="card-title">
          {t(`${K}.title`)}
        </h2>
        <p className="mt-1 text-xs text-fg-muted">{t(`${K}.intro`)}</p>
      </div>
    </div>
  );
}

function LiveState({ canView, slug }: { canView: boolean; slug: string }) {
  const { t } = useTranslation();
  const webhooks = useWebhookEndpoints(canView);
  if (!canView) return <p className="text-xs text-fg-muted">{t(`${K}.needsView`)}</p>;
  if (webhooks.isPending) return <Skeleton className="h-5 w-2/3" />;
  if (webhooks.isError) {
    return <ErrorState headingLevel={3} title={t(`${K}.failedTitle`)} description={t(`${K}.failedBody`)} onRetry={() => void webhooks.refetch()} retryLabel={t("common.retry")} />;
  }
  if (webhooks.data.length === 0) {
    return (
      <EmptyState
        icon="webhook"
        headingLevel={3}
        title={t(`${K}.emptyTitle`)}
        description={t(`${K}.emptyBody`)}
        action={
          <AppLink href={consoleRoutes.settings(slug, "webhooks")} className={buttonClasses({ variant: "secondary" })}>
            <Icon name="plus" />
            {t(`${K}.emptyAction`)}
          </AppLink>
        }
      />
    );
  }
  const receiving = webhooks.data.filter((hook) => hook.enabled && hook.events.some(carriesCustomerRef)).length;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2" data-testid="webhook-ref-live">
        <Badge tone={receiving > 0 ? "success" : "neutral"} dot>
          {receiving > 0 ? t(`${K}.active`, { count: receiving }) : t(`${K}.noneActive`)}
        </Badge>
      </div>
      <AppLink href={consoleRoutes.settings(slug, "webhooks")} className={buttonClasses({ variant: "secondary" })}>
        <Icon name="webhook" />
        {t(`${K}.manage`)}
      </AppLink>
    </div>
  );
}

/** Settings > Privacy > "Webhooks carry the customer reference" (privacy-security pin 4, PDI-008): a signed sample payload and how many endpoints receive `customer_ref`. */
export function WebhookReferenceCard() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const canView = usePermissions().can("settings.view");
  return (
    <section className="card" aria-labelledby="webhook-ref-title" data-testid="webhook-ref-card">
      <Header />
      <div className="space-y-4 px-5 py-4">
        <CodeSnippet code={SAMPLE_EVENT} label={t(`${K}.sampleLabel`)} />
        <p className="flex items-start gap-2 text-xs text-fg-muted">
          <Icon name="info" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
          <span>{t(`${K}.carries`)}</span>
        </p>
        <LiveState canView={canView} slug={summary.slug} />
      </div>
    </section>
  );
}
